import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import { STORAGE_BASE_URL } from '../../config/api';
import api from '../../config/api';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import notificationService from '../../services/notificationService';

const EditProfile = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [imageTimestamp, setImageTimestamp] = useState(Date.now());
    const [unreadCount, setUnreadCount] = useState(0);
    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            phone: '',
            email: '',
        },
    });


    const loadUnreadCount = async () => {
        try {
            const response = await notificationService.getUnreadCount();
            if (response.success) {
                setUnreadCount(response.count);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    useEffect(() => {
        loadProfile();
    }, []);

    useEffect(() => {
        loadUnreadCount();
    }, []);

    const loadProfile = async () => {
        try {
            setProfileLoading(true);
            const response = await authService.getProfile();
            if (response.success && response.data?.user) {
                const userData = response.data.user;
                setProfileData(userData);
                setValue('name', userData.name || '');
                setValue('phone', userData.phone || '');
                setValue('email', userData.email || '');
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error(t('employee.editProfile.loadFailed'));
        } finally {
            setProfileLoading(false);
        }
    };

    const fileInputRef = useRef(null);
    const handleProfileImageClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

            const loadingToast = toast.loading(t('employee.editProfile.uploadingImage'));

        try {
            const formData = new FormData();
            formData.append('image', file);
            // Use auth profile endpoint for image upload
            api.post('/auth/profile?_method=PUT', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                },
            });
            toast.dismiss(loadingToast);
            toast.success(t('employee.editProfile.imageUploadSuccess'));
            setImageTimestamp(Date.now());
            loadProfile();
        } catch (error) {
            toast.dismiss(loadingToast);
            const message = error.response?.data?.message || t('employee.editProfile.imageUploadFailed');
            toast.error(message);
        }
    }

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            const response = await authService.updateProfile(data);
            if (response.success) {
                // Update localStorage with new user data
                if (response.data?.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    // Trigger event to notify components of user data update
                    window.dispatchEvent(new Event('userDataUpdated'));
                }
                toast.success(t('employee.editProfile.updateSuccess'));
                navigate(-1); // Go back to previous page
            } else {
                toast.error(t('employee.editProfile.updateFailed'));
            }
        } catch (error) {
            console.error('Update profile error:', error);
            const message = error.response?.data?.message || error.message || t('employee.editProfile.updateFailed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const userImageRaw = profileData?.image || user?.image;
    const userImage = userImageRaw ? (userImageRaw.startsWith('http')
        ? `${userImageRaw}?t=${imageTimestamp}`
        : `${STORAGE_BASE_URL}/${userImageRaw}?t=${imageTimestamp}`) : null;
    const userName = profileData?.name || user?.name || '';

    if (profileLoading) {
        return <Loader fullScreen={true} size="large" />;
    }

    return (
        <div className="edit-profile-page">
            {/* Header */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir='ltr'>
                <div className="notification-icon">
                    <button
                        onClick={() => navigate('/employee/notifications')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <span className="bell-icon">🔔</span>
                        {unreadCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
                <div className="settings-title">{t('employee.editProfile.title')}</div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="edit-profile-form">
                {/* Profile Picture */}
                <div className="edit-profile-picture-container d-flex justify-content-center mb-4">
                    <div className="edit-profile-picture-large position-relative">
                        {userImage ? (
                            <img
                                src={userImage}
                                alt="Profile"
                                className="edit-profile-img-large"
                                key={userImage}
                            />
                        ) : (
                            <div className="edit-profile-img-large text-center align-content-center">
                                <b style={{ fontSize: '4rem' }}>{userName.charAt(0).toUpperCase()}</b>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleProfileImageChange}
                        />
                        <button
                            type="button"
                            className="edit-profile-camera-btn"
                            onClick={handleProfileImageClick}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                <circle cx="12" cy="13" r="4"></circle>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Phone Number Field */}
                <div className="edit-profile-field-container px-3 mb-3">
                    <label className="edit-profile-label">{t('employee.editProfile.phone')}</label>
                    <Controller
                        name="phone"
                        control={control}
                        rules={{
                            required: t('employee.editProfile.phoneRequired'),
                        }}
                        render={({ field }) => (
                            <PhoneInput
                                {...field}
                                international
                                defaultCountry="EG"
                                className="edit-profile-phone-input"
                                inputClassName="edit-profile-phone-input-field"
                                countrySelectProps={{ className: 'edit-profile-phone-country' }}
                                placeholder={t('employee.editProfile.phonePlaceholder')}
                            />
                        )}
                    />
                    {errors.phone && (
                        <span className="edit-profile-error">{errors.phone.message}</span>
                    )}
                </div>

                {/* Email Field */}
                <div className="edit-profile-field-container px-3 mb-4">
                    <label className="edit-profile-label">{t('employee.editProfile.email')}</label>
                    <input
                        type="email"
                        {...register('email', {
                            required: t('employee.editProfile.emailRequired'),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('employee.editProfile.emailInvalid'),
                            },
                        })}
                        className="edit-profile-input"
                        placeholder={t('employee.editProfile.emailPlaceholder')}
                    />
                    {errors.email && (
                        <span className="edit-profile-error">{errors.email.message}</span>
                    )}
                </div>

                {/* Save Button */}
                <div className="edit-profile-save-container px-3">
                    <button
                        type="submit"
                        className="edit-profile-save-btn"
                        disabled={loading}
                    >
                        {loading ? t('employee.editProfile.saving') : t('employee.editProfile.save')}
                    </button>
                </div>
            </form>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default EditProfile;

